import { useId, useState, type ReactNode } from "react";
import type { CreateOrganizationResponse, PlatformOrganization } from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { ApiRequestError } from "../../api/http";
import { platformOrganizationsQuery, useCreateOrganization, useOrganizationTransition, type OrganizationTransition } from "../../api/queries";
import { AboutBox } from "../../design/components/AboutBox";
import { Button } from "../../design/components/Button";
import { DataTable } from "../../design/components/DataTable";
import { Icon } from "../../design/components/Icon";
import { PageHeader } from "../../design/components/PageHeader";
import { Reason } from "../../design/components/Reason";
import { StatusPill } from "../../design/components/StatusPill";
import { useToast } from "../../design/components/Toast";
import type { Tone } from "../../design/components/tone";
import { messageForError } from "../../i18n/errors";
import { deviceTimeZone, formatNumericDate } from "../../i18n/format";
import { useT, type Translate } from "../../i18n/locale";
import { switchTab } from "../../session/switch-tab";
import { ManualLinkDialog } from "../manual-link-dialog";
import { ConfirmTransitionDialog, CreateOrganizationDialog, ManageOrganizationDialog } from "./organization-dialogs";
import { OrganizationTelephonyDialog } from "./organization-telephony";
import { OrganizationWhatsAppDialog } from "./organization-whatsapp";
import "./platform.css";

// «Aziende di Gioia365» (piano M1b Task 11, senza mock-up: segue lo stile del Team): l'elenco delle aziende per il
// Super admin di Gioia365, che le crea, le sospende, le riattiva e le chiude, e da qui «Entra» in una di esse. Le
// rotte sono di piattaforma (nessun header dell'azienda) e la guardia della rotta `piattaforma` lascia entrare
// soltanto lui; il server controlla comunque ogni richiesta. La pagina non ha l'azienda della scheda e le righe non
// portano il fuso della loro azienda: le date, in numeri come nel resto del prodotto, seguono il fuso del dispositivo.
//
// «Numeri WhatsApp» (piano M6, R2, Task 7b; questione 414) sta accanto a «Telefonia» soltanto dove i numeri ammessi dei WhatsApp
// automatici sono richiesti: fuori dalla produzione, o con `WHATSAPP_RECIPIENT_ALLOWLIST_REQUIRED=true`. Lo dice il server con
// l'elenco delle aziende (`whatsappAllowlistRequired`), uguale per tutte perché dipende dall'ambiente.

/** La finestra aperta, con la riga com'era all'apertura: una rilettura dell'elenco non la chiude sotto le mani. */
type Panel =
  | { kind: "create" }
  | { kind: "manage"; organization: PlatformOrganization }
  | { kind: "telephony"; organization: PlatformOrganization }
  | { kind: "whatsapp"; organization: PlatformOrganization }
  | { kind: "confirm"; action: "suspend" | "close"; organization: PlatformOrganization };

/** Il link dell'invito del titolare da copiare (l'email non è partita). */
type OwnerLink = { organization: string; owner: string; link: string; expiresAt: string };

/** Ciò che prepara un database in questo momento: la riga di cui si riprende la preparazione, o una creazione (`id` nullo). */
type Running = { id: string | null; name: string };

type Look = { tone: Tone; icon: "check" | "clock" | "minus" | "alert"; label: MessageKey };

/** Come si dice lo stato di un'azienda, con tono, icona e testo. */
function statusLook(row: PlatformOrganization): Look {
  switch (row.status) {
    case "provisioning":
      // Con un errore registrato la preparazione si è fermata; senza, sta andando avanti (o si è interrotta senza dirlo).
      return row.provisioningError === null
        ? { tone: "warning", icon: "clock", label: "platform.status.provisioning" }
        : { tone: "warning", icon: "alert", label: "platform.status.stalled" };
    case "active":
      return { tone: "success", icon: "check", label: "platform.status.active" };
    case "suspending":
      // Il primo passo è fatto (nessuno entra più); il resto lo completa il server.
      return { tone: "warning", icon: "clock", label: row.pendingTransition === "closed" ? "platform.status.closing" : "platform.status.suspending" };
    case "suspended":
      return { tone: "danger", icon: "minus", label: "platform.status.suspended" };
    case "closed":
      return { tone: "neutral", icon: "minus", label: "platform.status.closed" };
  }
}

/** Chi sta uscendo (sospensione o chiusura in corso) conta già fra le sospese o le chiuse. */
function counts(rows: readonly PlatformOrganization[]) {
  const result = { total: rows.length, active: 0, suspended: 0, closed: 0, preparing: 0 };
  for (const row of rows) {
    if (row.status === "active") result.active += 1;
    else if (row.status === "provisioning") result.preparing += 1;
    else if (row.status === "closed" || (row.status === "suspending" && row.pendingTransition === "closed")) result.closed += 1;
    else result.suspended += 1;
  }
  return result;
}

/** Il testo di un errore delle azioni di stato: `invalid_state` dice che la riga era vecchia. */
function actionError(t: Translate, error: unknown): string {
  return error instanceof ApiRequestError && error.code === "invalid_state" ? t("platform.errors.invalid_state") : messageForError(t, error);
}

/** L'indirizzo con cui si apre un'azienda: l'host di questo programma e l'indirizzo breve (`/<slug>` nel router). */
function companyAddress(slug: string): string {
  return `${window.location.host}/${slug}`;
}

export function PlatformOrganizationsPage() {
  const t = useT();
  const router = useRouter();
  const toast = useToast();
  const titleId = useId();
  const organizations = useQuery(platformOrganizationsQuery);
  const allowlistRequired = organizations.data?.whatsappAllowlistRequired === true;
  const transition = useOrganizationTransition();
  const create = useCreateOrganization();
  const [panel, setPanel] = useState<Panel | null>(null);
  // I link dei titolari in coda: si mostrano uno alla volta, appena nessun'altra finestra è aperta. Un link non
  // prende mai il posto di una finestra aperta (una creazione in corso, per esempio) e non si perde.
  const [links, setLinks] = useState<OwnerLink[]>([]);
  // Una sola preparazione alla volta (una creazione o la ripresa di una riga): finché dura, niente di ciò che
  // apre una finestra o cambia pagina si può avviare, e una riga dice quale azienda si sta preparando.
  const [running, setRunning] = useState<Running | null>(null);
  const [entering, setEntering] = useState<string | null>(null);
  const timeZone = deviceTimeZone();
  const rows = organizations.data?.organizations;
  const close = () => setPanel(null);
  const owner = panel === null ? links[0] : undefined;
  const locked = running !== null;

  /**
   * Un'azienda è nata o la sua preparazione è ripresa: il link del titolare in coda, o l'avviso. Dalla finestra di
   * creazione la finestra si chiude; una ripresa dalla riga non tocca la finestra che fosse aperta.
   */
  function created({ organization, ownerInvitation }: CreateOrganizationResponse, fromDialog: boolean) {
    if (fromDialog) setPanel(null);
    const link = ownerInvitation.manualDelivery?.link;
    if (link) {
      setLinks((queue) => [...queue, { organization: organization.name, owner: organization.ownerName, link, expiresAt: ownerInvitation.expiresAt }]);
      return;
    }
    toast.show(t("platform.create.created", { email: ownerInvitation.email }), "success");
  }

  /** La creazione si è fermata a metà: l'azienda è già nell'elenco «in preparazione», con il suo pulsante. */
  function stalled() {
    setPanel(null);
    toast.show(t("errors.provisioning_failed"), "danger");
  }

  /**
   * «Riprendi la preparazione»: la stessa creazione, con l'indirizzo breve dell'azienda. La riga porta nome, indirizzo,
   * nome, cognome ed email del titolare; `timeZone` e `defaultLocale` ci sono soltanto per superare lo schema di creazione,
   * perché la riga non li porta. Il server, nella ripresa, usa i valori salvati alla prima transazione e ignora gli
   * altri campi del corpo (`organizations/service.ts`, ripresa della saga): non cambiano niente di ciò che il
   * Super admin di Gioia365 aveva scelto.
   */
  async function continueSetup(row: PlatformOrganization) {
    if (running) return;
    setRunning({ id: row.id, name: row.name });
    try {
      created(
        await create.mutateAsync({
          name: row.name,
          slug: row.slug,
          timeZone: "Europe/Rome",
          defaultLocale: "it",
          ownerEmail: row.ownerEmail,
          ownerFirstName: row.ownerFirstName,
          ownerLastName: row.ownerLastName,
        }),
        false,
      );
    } catch (error) {
      // `slug_taken` qui non vuol dire «indirizzo già usato»: un'altra scheda l'ha appena completata.
      const taken = error instanceof ApiRequestError && error.code === "slug_taken";
      toast.show(taken ? t("platform.errors.resumeTaken") : messageForError(t, error), "danger");
    } finally {
      setRunning((current) => (current?.id === row.id ? null : current));
    }
  }

  // Le azioni di stato chiudono prima la finestra, poi l'avviso dice com'è andata (D16). Le promesse e non i
  // callback di `mutate`, che valgono soltanto per l'ultima chiamata: due azioni di fila hanno due avvisi.
  function changeStatus(organization: PlatformOrganization, action: OrganizationTransition) {
    setPanel(null);
    const name = organization.name;
    const done: Record<OrganizationTransition, string> = {
      suspend: t("platform.done.suspended", { organization: name }),
      resume: t("platform.done.resumed", { organization: name }),
      close: t("platform.done.closed", { organization: name }),
    };
    void transition.mutateAsync({ organizationId: organization.id, action }).then(
      () => toast.show(done[action], "success"),
      (error: unknown) => toast.show(actionError(t, error), "danger"),
    );
  }

  /** «Entra»: la scheda lavora in quell'azienda come Super admin di Gioia365; la Home lo dice con l'avviso di D13. */
  async function enter(organization: PlatformOrganization) {
    if (entering) return;
    setEntering(organization.id);
    try {
      await switchTab(router, { kind: "platform", organizationId: organization.id });
    } finally {
      setEntering(null);
    }
  }

  let content: ReactNode;
  if (organizations.isError) {
    content = (
      <div className="g-platform__problem">
        <Reason tone="warning" role="alert">{messageForError(t, organizations.error)}</Reason>
        <Button onClick={() => void organizations.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else if (!rows) {
    content = <p className="g-platform__counts">{t("common.loading")}</p>;
  } else if (rows.length === 0) {
    content = <p>{t("platform.list.empty")}</p>;
  } else {
    content = (
      <DataTable caption={t("platform.list.caption")} captionHidden>
        <thead>
          <tr>
            <th scope="col">{t("platform.columns.company")}</th>
            <th scope="col">{t("platform.columns.status")}</th>
            <th scope="col">{t("platform.columns.owner")}</th>
            <th scope="col">{t("platform.columns.people")}</th>
            <th scope="col">{t("platform.columns.created")}</th>
            <th scope="col">
              <span className="g-visually-hidden">{t("platform.columns.action")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const look = statusLook(row);
            const { name } = row;
            return (
              <tr key={row.id}>
                <th scope="row">
                  <strong>{name}</strong>
                  {/* L'indirizzo dell'azienda da dare a chi ci lavora (`/<slug>`, decisione del 3 ottobre 2026). */}
                  <span className="g-table__sub">{companyAddress(row.slug)}</span>
                </th>
                <td>
                  <StatusPill tone={look.tone} icon={look.icon}>{t(look.label)}</StatusPill>
                </td>
                <td>
                  <span>{row.ownerName}</span>
                  <span className="g-table__sub">{row.ownerEmail}</span>
                </td>
                <td className="g-platform__number">{row.activeMemberships}</td>
                <td>{formatNumericDate(timeZone, row.createdAt)}</td>
                <td>
                  <span className="g-platform__actions">
                    {row.status === "provisioning" ? (
                      <Button
                        onClick={() => void continueSetup(row)}
                        disabled={locked}
                        aria-busy={running?.id === row.id ? "true" : undefined}
                        aria-label={t("platform.row.continueSetupNamed", { organization: name })}
                      >
                        {t("platform.row.continueSetup")}
                      </Button>
                    ) : null}
                    {row.status === "active" ? (
                      <Button onClick={() => void enter(row)} disabled={locked || entering !== null} aria-label={t("platform.row.enterNamed", { organization: name })}>
                        {t("platform.row.enter")}
                      </Button>
                    ) : null}
                    {/* Su ogni riga, anche sospesa o chiusa: il pannello la mostra e la spegne sempre; accenderla, riprenderla e verificare il numero il server li consente soltanto a un'azienda attiva, e il pannello lo dice. */}
                    <Button onClick={() => setPanel({ kind: "telephony", organization: row })} disabled={locked} aria-label={t("platformTelephony.title", { organization: name })}>
                      {t("platformTelephony.open")}
                    </Button>
                    {allowlistRequired ? (
                      <Button onClick={() => setPanel({ kind: "whatsapp", organization: row })} disabled={locked} aria-label={t("platformWhatsApp.title", { organization: name })}>
                        {t("platformWhatsApp.open")}
                      </Button>
                    ) : null}
                    {row.status === "active" || row.status === "suspended" ? (
                      <Button onClick={() => setPanel({ kind: "manage", organization: row })} disabled={locked} aria-label={t("platform.row.manageNamed", { organization: name })}>
                        {t("platform.row.manage")}
                      </Button>
                    ) : null}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </DataTable>
    );
  }

  const summary = rows && rows.length > 0 ? counts(rows) : null;

  return (
    <>
      <PageHeader eyebrow={t("platform.eyebrow")} title={t("platform.title")} />
      <AboutBox title={t("platform.about.title")}>
        <p>{t("platform.about.text")}</p>
      </AboutBox>
      <section className="g-platform" aria-labelledby={titleId}>
        <div className="g-platform__head">
          <div className="g-platform__title">
            <h2 id={titleId}>{t("platform.list.title")}</h2>
            {summary ? <p className="g-platform__counts">{t("platform.list.counts", summary)}</p> : null}
          </div>
          <Button variant="primary" onClick={() => setPanel({ kind: "create" })} disabled={locked}>
            <Icon name="plus" />
            {t("platform.new")}
          </Button>
        </div>
        {/* Sempre nella pagina e vuota finché non serve: uno screen reader annuncia soltanto ciò che vi compare. */}
        <p className="g-platform__wait" role="status">
          {running ? t("platform.list.preparing", { organization: running.name }) : null}
        </p>
        {content}
      </section>

      {panel?.kind === "create" ? (
        <CreateOrganizationDialog
          onClose={close}
          onCreated={(response) => created(response, true)}
          onStalled={stalled}
          onRunningChange={(name) => setRunning((current) => (name !== null ? { id: null, name } : current?.id === null ? null : current))}
        />
      ) : null}
      {panel?.kind === "manage" ? (
        <ManageOrganizationDialog
          // La riga riletta dopo «Cambia nome», così titolo e campi mostrano il nome nuovo.
          organization={rows?.find((row) => row.id === panel.organization.id) ?? panel.organization}
          onClose={close}
          onStatus={(action) => {
            // Riattivare agisce subito; sospendere e chiudere chiedono conferma.
            if (action === "resume") changeStatus(panel.organization, action);
            else setPanel({ kind: "confirm", action, organization: panel.organization });
          }}
        />
      ) : null}
      {panel?.kind === "telephony" ? <OrganizationTelephonyDialog organization={panel.organization} onClose={close} /> : null}
      {panel?.kind === "whatsapp" ? <OrganizationWhatsAppDialog organization={panel.organization} onClose={close} /> : null}
      {panel?.kind === "confirm" ? (
        <ConfirmTransitionDialog
          action={panel.action}
          organization={panel.organization.name}
          onCancel={close}
          onConfirm={() => changeStatus(panel.organization, panel.action)}
        />
      ) : null}
      {owner ? (
        <ManualLinkDialog
          // Un link nuovo, una finestra nuova: «Copiato» non resta dal link di prima.
          key={owner.link}
          open
          title={t("platform.ownerLink.title")}
          intro={t("platform.ownerLink.intro", {
            organization: owner.organization,
            name: owner.owner,
            date: formatNumericDate(timeZone, owner.expiresAt),
          })}
          link={owner.link}
          onClose={() => setLinks((queue) => queue.slice(1))}
        />
      ) : null}
    </>
  );
}
